/**
 * QuickRepliesRenderer — chip-row for one-of multiple-choice prompts.
 *
 * Live state:
 *   - Live (`!ctx.isHistory`): chips are tappable. Tapping fires
 *     `onSendMessage(label, {in_response_to: prompt_id, structured_value: value})`.
 *     The agent receives the value as structured metadata and the label
 *     appears as the user's reply in the transcript.
 *   - History: chips render inert. The chip whose value matches
 *     `ctx.answeredWith` shows highlighted, the rest muted.
 *
 * `allow_typed` is informational for the renderer — the typed-input box
 * lives in ChatView and stays usable regardless. We surface it here only
 * to flip aria-pressed semantics when the chips are the only path.
 */
import React from 'react';
import type { QuickRepliesContentItem, QuickReplyOption } from '@wg/types';
import type { Renderer } from '../registry';

const QuickRepliesRenderer: Renderer = ({ item, ctx }) => {
  const it = item as QuickRepliesContentItem;
  if (!it.options || it.options.length === 0) return null;

  // Suppress inline render when this prompt is currently hoisted into the
  // docked chip strip above the input. Live turns dock; history stays
  // inline so the user sees what they tapped.
  if (!ctx.isHistory && ctx.dockedPromptIds?.has(it.prompt_id)) return null;

  const inert = ctx.isHistory;
  const answered = ctx.answeredWith;

  return (
    <div
      className="wg-chat__chips"
      data-inert={inert ? 'true' : undefined}
      role="group"
      aria-label={it.question || 'Quick replies'}
    >
      {it.question && <p className="wg-chat__chips-q">{it.question}</p>}
      <div className="wg-chat__chips-row">
        {it.options.map((opt: QuickReplyOption, idx: number) => {
          const isChosen = answered !== undefined && answered === opt.value;
          const cls = [
            'wg-chat__chip',
            opt.weight ? `wg-chat__chip--${opt.weight}` : null,
            isChosen ? 'is-chosen' : null,
            inert && !isChosen ? 'is-muted' : null,
          ]
            .filter(Boolean)
            .join(' ');
          return (
            <button
              key={`${opt.value}-${idx}`}
              type="button"
              className={cls}
              disabled={inert}
              aria-pressed={isChosen || undefined}
              onClick={() => {
                if (inert) return;
                ctx.onSendMessage(opt.label, {
                  in_response_to: it.prompt_id,
                  structured_value: opt.value,
                });
              }}
            >
              {opt.icon && (
                <span className="wg-chat__chip-icon">{opt.icon}</span>
              )}
              <span className="wg-chat__chip-label">{opt.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default QuickRepliesRenderer;
